import { ChangeDetectionStrategy, Component, OnInit, inject, signal, DestroyRef, SecurityContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, of } from 'rxjs';

import { AccessibilityService } from '@shared/services/accessibility.service';
import { PortalConfig } from '@shared/portal-config';
import { ModalWrapperComponent } from '@components/views/modal-wrapper/modal-wrapper.component';
import { LoadingAnimationComponent } from '@shared/components/loading-animation/loading-animation.component';
import { sanitizeHtmlUrls } from '@shared/utils/html-security.utils';

@Component({
    selector: 'app-accessibility-modal',
    templateUrl: './accessibility-modal.component.html',
    styleUrls: ['./accessibility-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ModalWrapperComponent, LoadingAnimationComponent]
})
export class AccessibilityModalComponent implements OnInit {
    
    // Reactive signals for state management
    public readonly accessibilityContent = signal<string>('');
    public readonly dataLoading = signal<boolean>(true);
    public readonly dataAvailable = signal<boolean>(false);
    public readonly dataEmpty = signal<boolean>(false);
    public readonly supportMessage = signal<string>('');

    // Injected dependencies using modern Angular 20 patterns
    private readonly _accessibilityService = inject(AccessibilityService);
    private readonly _portalConfig = inject(PortalConfig);
    private readonly _sanitizer = inject(DomSanitizer);
    private readonly _destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.initializeComponent();
        this.loadAccessibilityContent();
    }

    private initializeComponent(): void {
        this.dataLoading.set(true);
        this.dataAvailable.set(false);
        this.dataEmpty.set(false);
        this.supportMessage.set(this._portalConfig.supportMessage);
    }

    private loadAccessibilityContent(): void {
        this._accessibilityService.getAccessibilityContent()
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                catchError(error => {
                    console.error('Error loading accessibility content:', error);
                    this.handleEmptyOrErrorContent();
                    return of(null);
                })
            )
            .subscribe(response => {
                if (response?.content && response?.isAccessibilityEnabled) {
                    this.handleSuccessfulLoad(response.content);
                } else {
                    this.handleEmptyOrErrorContent();
                }
            });
    }

    private handleSuccessfulLoad(content: string): void {
        // Layer 1: Angular's built-in DomSanitizer (strips scripts, event handlers, dangerous protocols)
        const angularSanitized = this._sanitizer.sanitize(SecurityContext.HTML, content);
        // Layer 2: Enforce URL scheme allowlist (strips relative URLs that could trigger API calls)
        const sanitizedContent = angularSanitized ? sanitizeHtmlUrls(angularSanitized) : '';

        if (!sanitizedContent) {
            this.handleEmptyOrErrorContent();
            return;
        }

        this.accessibilityContent.set(sanitizedContent);
        this.dataLoading.set(false);
        this.dataAvailable.set(true);
        this.dataEmpty.set(false);
    }

    private handleEmptyOrErrorContent(): void {
        this.accessibilityContent.set('');
        this.dataLoading.set(false);
        this.dataAvailable.set(false);
        this.dataEmpty.set(true);
    }
}
